"use client";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  Pie,
  PieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { FileText, CheckCircle2, AlertCircle, Wrench } from "lucide-react";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../Component/ui/card";
import "./allSection.css";
import axios from "axios";
import API_URL from "../../../src/Config";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="newdashboard-tooltip">
        <p className="newdashboard-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function AMCSection() {
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    active_contracts: { count: 0, label: "" },
    expired_contracts: { count: 0, label: "" },
    completed_jobs: { count: 0, label: "" },
    overdue_services: { count: 0, label: "" },
    top_department: { name: "", active_contracts: 0 },
  });

  const [charts, setCharts] = useState({
    amc_job_status: {
      active: { count: 0, percentage: 0 },
      completed: { count: 0, percentage: 0 },
      overdue: { count: 0, percentage: 0 },
    },
    monthly_scheduled_amc: [],
    department_wise_status: [],
  });

  // ✅ Centralized filter state
  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  // ✅ Fetch KPI data with filters
  const fetchAMCData = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams();

      if (currentFilters.date_preset)
        params.append("filter_option", currentFilters.date_preset);

      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);

      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);
      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);

      const response = await axios.get(
        `${API_URL}/api/kpi/amc-maintenance?${params.toString()}`,
        {
          headers: { Authorization: authKey },
        },
      );

      if (response.status === 200) {
        setSummary(response.data.summary || {});
        setCharts(response.data.charts || {});
      }
    } catch (error) {
      console.error("Error fetching AMC KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply filters
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchAMCData(newFilters);
  };

  // ✅ Reset filters
  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchAMCData(reset);
  };

  useEffect(() => {
    fetchAMCData();
  }, []);

  const appendSharedFilters = (params) => {
    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);

    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }

    if (
      filters.start_date &&
      filters.end_date &&
      filters.start_date.trim() !== "" &&
      filters.end_date.trim() !== ""
    ) {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }
  };

  const handleJobCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);
    appendSharedFilters(params);
    console.log("Navigating to AMC List with params:", params.toString());
    window.open(`/amc-list?${params.toString()}`, "_blank");
  };

  const handleContractCardClick = (status) => {
    const params = new URLSearchParams();
    params.append("status", status);
    appendSharedFilters(params);

    console.log(
      "Navigating to AMC Configuration with params:",
      params.toString(),
    );
    window.open(`/amc-configuration?${params.toString()}`, "_blank");
  };

  const formatPercentage = (value) => {
    const parsedValue = Number(value || 0);
    return `${parsedValue}% of AMC jobs`;
  };

  const amcStatusData = [
    {
      name: "Active",
      value: Number(charts.amc_job_status?.active?.count || 0),
      fill: "hsl(217, 91%, 60%)",
      percentage: charts.amc_job_status?.active?.percentage || 0,
    },
    {
      name: "Completed",
      value: Number(charts.amc_job_status?.completed?.count || 0),
      fill: "hsl(142, 71%, 45%)",
      percentage: charts.amc_job_status?.completed?.percentage || 0,
    },
    {
      name: "Overdue",
      value: Number(charts.amc_job_status?.overdue?.count || 0),
      fill: "hsl(0, 84%, 60%)",
      percentage: charts.amc_job_status?.overdue?.percentage || 0,
    },
  ];

  return (
    <div className="newdashboard-amc-section">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="newdashboard-amc-header-row">
            <div>
              <h2 className="newdashboard-section-title">
                Annual Maintenance Contracts (AMC)
              </h2>
              <p className="newdashboard-amc-subtitle">
                Track AMC contracts and service completion
              </p>
            </div>

            {/* ✅ Controlled Filters */}
            <DashboardFilters
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          {/* KPI Cards - Now Clickable */}
          <div className="newdashboard-amc-kpi-grid">
            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleJobCardClick("active_jobs")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Active AMC Jobs</CardTitle>
                <Wrench className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value-active">
                  {Number(charts.amc_job_status?.active?.count || 0)}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {formatPercentage(charts.amc_job_status?.active?.percentage)}
                </p>
              </CardContent>
            </Card>

            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleJobCardClick("completed")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Completed AMC Jobs</CardTitle>
                <CheckCircle2 className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value">
                  {Number(charts.amc_job_status?.completed?.count || 0)}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {formatPercentage(
                    charts.amc_job_status?.completed?.percentage,
                  )}
                </p>
              </CardContent>
            </Card>

            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleJobCardClick("overdue")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Overdue AMC Jobs</CardTitle>
                <AlertCircle className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value-destructive">
                  {Number(charts.amc_job_status?.overdue?.count || 0)}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {formatPercentage(charts.amc_job_status?.overdue?.percentage)}
                </p>
              </CardContent>
            </Card>

            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleContractCardClick("Active")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Active AMC Contracts</CardTitle>
                <FileText className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value">
                  {summary.active_contracts?.count ?? 0}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {summary.active_contracts?.label || ""}
                </p>
              </CardContent>
            </Card>

            <Card
              className="newdashboard-card-clickable"
              onClick={() => handleContractCardClick("Expired")}
              style={{ cursor: "pointer" }}
            >
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Expired AMC Contracts</CardTitle>
                <AlertCircle className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value-destructive">
                  {summary.expired_contracts?.count ?? 0}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {summary.expired_contracts?.label || ""}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="newdashboard-amc-card-header">
                <CardTitle>Top Department</CardTitle>
                <Wrench className="newdashboard-amc-icon" />
              </CardHeader>
              <CardContent>
                <div className="newdashboard-amc-kpi-value">
                  {summary.top_department?.name || "N/A"}
                </div>
                <p className="newdashboard-amc-kpi-subtext">
                  {summary.top_department?.active_contracts ?? 0} active
                  contracts
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="newdashboard-amc-charts-grid">
            <Card>
              <CardHeader>
                <CardTitle>AMC Job Status</CardTitle>
                <CardDescription>Active vs Completed vs Overdue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="newdashboard-card-content">
                  <div className="newdashboard-chart-container">
                    {amcStatusData.some((item) => Number(item.value) > 0) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={amcStatusData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percentage }) =>
                              `${name}: ${percentage}%`
                            }
                            outerRadius={100}
                            dataKey="value"
                          >
                            {amcStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="newdashboard-no-data">No Data Found</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Monthly Scheduled AMC</CardTitle>
                <CardDescription>
                  Total AMC services scheduled per month
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="newdashboard-card-content">
                  <div className="newdashboard-chart-container">
                    {charts.monthly_scheduled_amc?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={charts.monthly_scheduled_amc}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar
                            dataKey="scheduled_amc"
                            fill="hsl(217, 91%, 60%)"
                            name="Scheduled AMC"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="newdashboard-no-data">No Data Found</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Department Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Department-wise AMC Status</CardTitle>
              <CardDescription>
                Active, completed, and overdue AMC by department
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="newdashboard-card-content">
                <div className="newdashboard-chart-container">
                  {charts.department_wise_status?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={charts.department_wise_status}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="department"
                          stroke="#6b7280"
                          fontSize={12}
                        />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="active"
                          fill="hsl(217, 91%, 60%)"
                          name="Active"
                        />
                        <Bar
                          dataKey="completed"
                          fill="hsl(142, 71%, 45%)"
                          name="Completed"
                        />
                        <Bar
                          dataKey="overdue"
                          fill="hsl(0, 84%, 60%)"
                          name="Overdue"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="newdashboard-no-data">No Data Found</div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
